import React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { SmilePlus } from "lucide-react";

const EMOJIS = ["👍", "✅", "❤️", "🎉", "👀", "🙏"];

export const MessageReactions = ({ message, invalidateKey, dark }) => {
  const { user } = useAuth();
  const qc = useQueryClient();
  const reactions = message.reactions || {};

  const toggle = useMutation({
    mutationFn: (emoji) => api.post(`/chat/messages/${message.message_id}/react`, { emoji }),
    onSuccess: () => qc.invalidateQueries({ queryKey: invalidateKey }),
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not react."),
  });

  const entries = Object.entries(reactions).filter(([, u]) => (u || []).length);

  return (
    <div className="mt-1 flex flex-wrap items-center gap-1">
      {entries.map(([emoji, users]) => {
        const mine = users.includes(user?.user_id);
        return (
          <button
            key={emoji}
            onClick={() => toggle.mutate(emoji)}
            data-testid={`reaction-${message.message_id}-${emoji}`}
            className={cn("inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[11px] leading-none",
              mine ? "border-gold bg-gold/20 text-navy" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50")}
          >
            <span>{emoji}</span><span className="font-semibold">{users.length}</span>
          </button>
        );
      })}
      <Popover>
        <PopoverTrigger asChild>
          <button data-testid={`react-add-${message.message_id}`}
            className={cn("rounded-full p-1 opacity-0 transition-opacity group-hover:opacity-100",
              dark ? "text-white/70 hover:bg-white/10" : "text-slate-400 hover:bg-slate-100")}>
            <SmilePlus className="h-3.5 w-3.5" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-1">
          <div className="flex gap-0.5">
            {EMOJIS.map((e) => (
              <button key={e} onClick={() => toggle.mutate(e)} data-testid={`react-pick-${message.message_id}-${e}`}
                className="rounded p-1 text-lg leading-none hover:bg-slate-100">{e}</button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
